import {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {fabric} from 'fabric';
import cx from 'classnames';
import {toastAlert} from '~library/atoms/Toast';
import {GCS_PATH_IDS, gcsUploadImage} from '~/common/storage/gcs_storage';
import {AlertLineIcon} from '~library/svg/AlertLineIcon';
import {
  SET_UPLOADED_IMAGE,
  UPDATE_DESIGN_OPTIONS,
} from '~/CreateFabricGreetingCard/reducers/fabricDesign/constants';
import {charcoal, mexicanOpalWebText} from '~libSass/base/_exports.sass';
import {FabricContext} from '~/common/fabric/context/FabricContext';
import {
  selectFabricArtBackgroundImage,
  selectIsGiphy,
  selectShouldUseCachedGalleryPhoto,
} from '~/CreateFabricGreetingCard/reducers/fabricDesign/selectors';
import {selectIsGiphyFeatureSwitchEnabled} from '~/CreateFabricGreetingCard/reducers/details/selectors';
import {PhotoOrGifModal} from '~library/organisms/PhotoOrGifModal';
import {fetchImageAsBlob} from '~/common/utils';
import {GiphyIcon} from '~library/svg/GiphyIcon';
import {UploadIcon} from '~library/svg/UploadIcon';
import {Typography} from '~library/atoms/Typography';
import {Button} from '~library/atoms/Button';
import {useIsMediumAndUp} from '~library/hooks/useMatchQuery';
import {useCacheGalleryImg} from '~/common/hooks/useCacheGalleryImg';
import {dataURLToFile} from '~/common/utils/dataURLToFile';

import './UploadButton.sass';

let isImgRendered = false;

export const imageFileUpload = async (file, eventId) => {
  if (file.size > 10 ** 7) {
    toastAlert('Please select an image less than 10MB.', {
      className: 'uyo_toast__error',
      title: 'This image is too large',
      type: 'error',
      icon: <AlertLineIcon ratio={0.65} color={mexicanOpalWebText} />,
      position: 'top-right',
      autoClose: 5000,
      hideProgressBar: true,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });
    throw new Error('Please select an image less than 10MB.');
  }

  const gcsUploadArgs = {
    gcsPathId: GCS_PATH_IDS.anonymous_image,
    params: {
      event_id: eventId,
      image_id: '',
    },
    image: file,
    fileExtension: file.type ? file.type.split('/').pop() : null,
  };

  return gcsUploadImage(gcsUploadArgs);
};

export const UploadButton = ({className, styles, callback, eventId, isInPhotoEditor = false}) => {
  const {canvas, activeObjects} = useContext(FabricContext);
  const [processing, setProcessing] = useState(false);
  const [showPhotoOrGifModal, setShowPhotoOrGifModal] = useState(false);
  const isGiphy = useSelector(selectIsGiphy);
  const backgroundImage = useSelector(selectFabricArtBackgroundImage);
  const isGiphyFeatureSwitchEnabled = useSelector(selectIsGiphyFeatureSwitchEnabled);
  const shouldUseCachedGalleryPhoto = useSelector(selectShouldUseCachedGalleryPhoto);
  const [id, setId] = useState(null);
  const fileRef = useRef();
  const dispatch = useDispatch();
  const isMediumAndUp = useIsMediumAndUp();
  const {galleryImg} = useCacheGalleryImg();

  const onClickHandler = useCallback(() => {
    if (isGiphyFeatureSwitchEnabled) {
      setShowPhotoOrGifModal(true);
    } else {
      fileRef.current.click();
    }
  }, [isGiphyFeatureSwitchEnabled]);

  useEffect(() => {
    if (activeObjects?.[0]?.type === 'uYOImage') {
      const [activeImage] = activeObjects;
      setId(activeImage.id);
    }
  }, [activeObjects]);

  const onPhotoModalClose = useCallback(() => {
    setTimeout(() => {
      setShowPhotoOrGifModal(false);
    }, 0);
  }, []);

  const setImgClipPathIfNeeded = useCallback((originalObject, img, bkgImage) => {
    const isAnimated = bkgImage.src?.endsWith('.gif');

    if (originalObject && isAnimated) {
      // eslint-disable-next-line no-param-reassign
      img.clipPath = new fabric.Rect({
        left: originalObject.left - img.left,
        top: originalObject.top - img.top,
        width: originalObject.width * originalObject.scaleX,
        height: originalObject.height * originalObject.scaleY,
        originX: 'left',
        originY: 'top',
        absolutePositioned: true,
      });
    }
  }, []);

  const setUYOImage = useCallback(
    (img, isGiphyImg) => {
      const uyoImage = canvas.getObjects().find((obj) => obj.type === 'uYOImage');
      // eslint-disable-next-line no-underscore-dangle
      const originalObject = uyoImage?._originalObject || uyoImage.toJSON();

      const effectiveWidth = originalObject.width * originalObject.scaleX;
      const effectiveHeight = originalObject.height * originalObject.scaleY;
      const scaleX = effectiveWidth / img.width;
      const scaleY = effectiveHeight / img.height;
      let scale = Math.min(scaleX, scaleY);
      if (isGiphyImg) {
        scale *= 2;
      }

      setImgClipPathIfNeeded(originalObject, img, backgroundImage);

      if (!canvas.getObjects().find((obj) => obj.type === 'rect')) {
        const rect = new fabric.Rect({
          left: originalObject.left,
          top: originalObject.top,
          id,
          width: originalObject.width,
          height: originalObject.height,
          scaleX: originalObject.scaleX,
          scaleY: originalObject.scaleY,
          fill: 'white',
          selectable: false,
          hasControls: false,
        });

        canvas.insertAt(rect, 0);
        canvas.renderAll();
      }

      const centerX = originalObject.left + (originalObject.width * originalObject.scaleX) / 2;
      const centerY = originalObject.top + (originalObject.height * originalObject.scaleY) / 2;

      setTimeout(() => {
        img.scale(scale).set({
          left: centerX,
          top: centerY,
          originX: 'center',
          originY: 'center',
          centeredRotation: true,
          selectable: true,
          hasControls: false,
          _originalObject: originalObject,
        });

        canvas.remove(uyoImage);
        canvas.insertAt(img, 1);
        canvas.setActiveObject(img);
        canvas.renderAll();
        canvas.fire('object:added');
        setShowPhotoOrGifModal(false);
        setProcessing(false);
        callback?.(img);
      }, 200);
    },
    [canvas, setImgClipPathIfNeeded, backgroundImage, callback, id]
  );

  const onChangeImage = useCallback(
    async (e, giphyUrl) => {
      let url;
      let gcsPath;
      let imageKey;
      let file;
      try {
        if (giphyUrl) {
          dispatch({
            type: UPDATE_DESIGN_OPTIONS,
            payload: {isGiphy: true},
          });
          file = await fetchImageAsBlob(giphyUrl);
        } else {
          dispatch({
            type: UPDATE_DESIGN_OPTIONS,
            payload: {isGiphy: false},
          });
          [file] = e.target?.files || [];
        }
        if (file) {
          setProcessing(true);
          ({url, gcsPath, imageKey} = await imageFileUpload(file, eventId));
        }
        fabric.UYOImage.fromURL(url.split('?')[0], (img) => setUYOImage(img, !!giphyUrl));

        dispatch({
          type: SET_UPLOADED_IMAGE,
          payload: {url, gcsPath, imageKey},
        });
      } catch (error) {
        evite.error(error);
        setProcessing(false);
      }
    },
    [dispatch, eventId, setUYOImage]
  );

  useEffect(() => {
    async function uploadCachedImgToCloud() {
      try {
        setProcessing(true);
        const {url, gcsPath, imageKey} = await imageFileUpload(
          dataURLToFile(galleryImg, 'anonymous_image'),
          eventId
        );
        fabric.UYOImage.fromURL(url, setUYOImage);

        dispatch({
          type: SET_UPLOADED_IMAGE,
          payload: {url, gcsPath, imageKey},
        });
        dispatch({
          type: UPDATE_DESIGN_OPTIONS,
          payload: {shouldUseCachedGalleryPhoto: false},
        });
      } finally {
        setProcessing(false);
      }
    }

    if (galleryImg?.length > 0 && shouldUseCachedGalleryPhoto && !isImgRendered) {
      // TODO: Move isImgRendered state change into a then() statement on uploadCachedImgToCloud()
      uploadCachedImgToCloud();
      isImgRendered = true;
    }
  }, [galleryImg, shouldUseCachedGalleryPhoto, dispatch]);

  const buttonContent = useMemo(() => {
    if (isInPhotoEditor) {
      return (
        <>
          <UploadIcon className="action_button__upload" color={charcoal} ratio={0.5} />
          <Typography
            classNames="uyo-upload-button__text"
            as="p"
            variant="paragraph3"
            size="medium"
            color="charcoal"
          >
            {`Upload${isMediumAndUp ? ' new' : ''}`}
          </Typography>
        </>
      );
    }

    return isGiphyFeatureSwitchEnabled ? (
      <>
        <UploadIcon color={charcoal} ratio={0.5} />
        <Typography className="uyo-upload-btn__text" variant="paragraph3" semibold>
          Upload photo or
          <GiphyIcon ratio={0.325} />
        </Typography>
      </>
    ) : (
      <UploadIcon className="action_button__upload" color={charcoal} ratio={0.5} />
    );
  }, [isGiphyFeatureSwitchEnabled, isInPhotoEditor, isMediumAndUp]);

  return (
    <div style={styles} className={cx({'uyo-control-btn--giphy': isGiphyFeatureSwitchEnabled})}>
      <Button
        id="uyoBtn"
        variant="unstyled-button"
        className={isInPhotoEditor ? 'uyo-control-btn uyo-control-btn--upload' : className}
        onClick={onClickHandler}
        data-qa-id="uyo-upload-btn"
      >
        <input
          ref={fileRef}
          type="file"
          id="uyo-upload-input"
          accept="image/png,image/jpeg"
          onChange={onChangeImage}
          hidden
        />
        {processing ? <span className="evt-spinner inline no-margin" /> : buttonContent}
        {showPhotoOrGifModal && (
          <PhotoOrGifModal
            ref={fileRef}
            processing={processing}
            onHandleClose={onPhotoModalClose}
            onChangeImage={onChangeImage}
            isGiphy={isGiphy}
          />
        )}
      </Button>
    </div>
  );
};
